package net.gorillagroove.notification

import kotlinx.serialization.Serializable
import net.gorillagroove.GGCommonInternal
import net.gorillagroove.api.Api
import net.gorillagroove.api.PlaylistId
import net.gorillagroove.api.TrackApiId
import net.gorillagroove.api.UserNotificationId
import net.gorillagroove.db.*
import net.gorillagroove.db.Database.trackDao
import net.gorillagroove.db.Database.userNotificationDao
import net.gorillagroove.playlist.PlaylistOwnershipType
import net.gorillagroove.playlist.PlaylistService
import net.gorillagroove.sync.EntityChangeContent
import net.gorillagroove.sync.SyncCoordinator
import net.gorillagroove.sync.SyncDirection
import net.gorillagroove.sync.SyncPriority
import net.gorillagroove.sync.SyncableEntity
import net.gorillagroove.sync.strategies.PlaylistResponse
import net.gorillagroove.sync.strategies.PlaylistSyncStrategy
import net.gorillagroove.sync.strategies.PlaylistTrackResponse
import net.gorillagroove.sync.strategies.PlaylistTrackSyncStrategy
import net.gorillagroove.sync.strategies.PlaylistUserResponse
import net.gorillagroove.sync.strategies.ReviewSourceResponse
import net.gorillagroove.sync.strategies.ReviewSourceSyncStrategy
import net.gorillagroove.sync.strategies.TrackResponse
import net.gorillagroove.sync.strategies.TrackSyncStrategy
import net.gorillagroove.sync.strategies.UserResponse
import net.gorillagroove.sync.strategies.UserSyncStrategy
import net.gorillagroove.track.toTrack
import net.gorillagroove.util.EventHandlerRegistration
import net.gorillagroove.util.GGLog.logCrit
import net.gorillagroove.util.GGLog.logDebug
import net.gorillagroove.util.GGLog.logError
import net.gorillagroove.util.GGLog.logInfo
import net.gorillagroove.util.Settings
import net.gorillagroove.util.TimeUtil.now
import net.gorillagroove.util.ggJson
import kotlin.jvm.JvmInline

typealias UserNotificationChangeHandler = (unreadCount: Int) -> Unit

object UserNotificationService {
    fun getNotifications(): List<UserNotification> {
        val dbNotifications = userNotificationDao.getNotifications().many()
            // Push notifications can be inserted with blank messages
            // before the full thing is eventually synced. Ignore them temporarily.
            // This is because batched notifications have a list of their IDs, but
            // not the messages for each individual notification. So we save a temporary
            // notification with a blank message until the sync fills it in.
            .filter { it.message.isNotBlank() }

        val playlistIds = dbNotifications.flatMap { it.playlistIds }.toSet()

        val validPlaylistIds = PlaylistService.findDbPlaylistsById(playlistIds)
            .map { it.id }
            .toSet()

        val trackIds = dbNotifications.flatMap { it.addedTrackIds }
        val tracks = trackDao.findByApiIds(trackIds).many().associateBy { it.apiId!! }

        return dbNotifications.map { db ->
            val type = db.type.asEnumeratedType()
            val data = db.getNotificationData(type, validPlaylistIds, tracks)

            UserNotification(
                id = db.id,
                message = db.message,
                read = db.read,
                createdAt = db.createdAt,
                updatedAt = db.updatedAt,
                type = type,
                data = data,
            )
        }
    }

    private fun DbUserNotification.getNotificationData(
        type: UserNotificationType,
        playlistIds: Set<PlaylistId>,
        tracks: Map<TrackApiId, DbTrack>,
    ): UserNotificationData {
        val ids = listOf(this.id)

        // There will be room for improvement here in the future, probably, but a lot of these
        // notification types CAN have more than one entity that they contain. If someone adds
        // tracks to playlists, they CAN add tracks to more than one.
        // If someone recommends you tracks, they COULD recommend more than one at once, etc.
        // We have the notification action just be the first one we find. So if someone
        // recommended you two tracks and you tap the notification, it'll just operate on
        // a single track. At least for now, I guess.
        if (type.isPlaylistType) {
            val playlistId = this.playlistIds.firstOrNull() ?: return NoActionNotificationData(ids)
            if (!playlistIds.contains(playlistId)) {
                return NoActionNotificationData(ids)
            }
            return PlaylistNotificationData(ids, playlistId)
        } else if (type == UserNotificationType.USER_RECOMMEND) {
            val trackId = this.addedTrackIds.firstOrNull() ?: return NoActionNotificationData(ids)
            val track = tracks[trackId] ?: return NoActionNotificationData(ids)

            return TrackNotificationData(ids, track = track.toTrack())
        }
        return NoActionNotificationData(ids)
    }

    fun getUnreadCount(): Int {
        return userNotificationDao.countUnreads().oneOrNull()?.toInt() ?: 0
    }

    fun markRead(ids: List<UserNotificationId>) {
        if (ids.isEmpty()) {
            return
        }

        logInfo("Marking ${ids.size} notifications as read")

        userNotificationDao.markRead(now(), ids)
        broadcast()

        Database.forceSave()

        // We don't send push notifications if a notification gets marked read before its delay is up.
        // Because of this, we should attempt to sync immediately to avoid getting errant pushes.
        if (ids.isNotEmpty() && !GGCommonInternal.isIntegrationTesting) {
            SyncCoordinator.syncAsync(
                types = setOf(SyncableEntity.USER_NOTIFICATION),
                priority = SyncPriority.ELEVATED,
                direction = SyncDirection.UP,
            )
        }
    }

    fun delete(ids: List<UserNotificationId>) {
        userNotificationDao.softDelete(now(), ids)
        broadcast()

        Database.forceSave()

        if (ids.isNotEmpty() && !GGCommonInternal.isIntegrationTesting) {
            SyncCoordinator.syncAsync(
                types = setOf(SyncableEntity.USER_NOTIFICATION),
                priority = SyncPriority.ELEVATED,
                direction = SyncDirection.UP,
            )
        }
    }

    // Notifications can be "synced" in two ways.
    // 1) The actual sync. Duh.
    // 2) Via a websocket event or a push notification tap.
    // With unlucky timing, we can mark a notification as read, and then get it synced again and flagged as
    // un-read. As someone who really doesn't like most notifications, this is unacceptable behavior.
    // So any time we save a notification, query first to make sure it's not already been read.
    // If it has been read (and is unsynchronized), just ignore the update. Notifications should be
    // essentially immutable anyway aside from that flag, and the deleted flag. And deletes don't use this check.
    internal fun save(notifications: Collection<DbUserNotification>, notify: Boolean = true) {
        val existingNotifications = userNotificationDao.findByIds(notifications.map { it.id }).many()
        val ignoreableIds = existingNotifications
            .filter { it.read && !it.synchronized }
            .map { it.id }
            .toSet()

        val notificationsToUpdate = notifications.filterNot { ignoreableIds.contains(it.id) }
        notificationsToUpdate.forEach {
            userNotificationDao.upsert(it)
        }

        if (notify) {
            broadcast()
        }
    }

    internal fun broadcast() {
        val unreadCount = getUnreadCount()
        eventRegistration.broadcast { it(unreadCount) }
    }

    suspend fun getSettings(): List<NotificationSetting> {
        val response = try {
            Api.get<NotificationSettingResponse>("user-notification/option")
        } catch (e: Exception) {
            logError("Failed to get notification settings", e)
            throw e
        }

        return response.items.map { it.toNotificationSetting() }
    }

    suspend fun updateSettings(settings: List<NotificationSetting>): Boolean {
        settings.forEach { setting ->
            Settings.setString(setting.settingMethodKey, setting.method.name)
            Settings.setInt(setting.settingBatchIntervalKey, setting.batchIntervalMinutes)
        }

        logInfo("Syncing new notification settings")
        val result = SyncCoordinator.sync(
            types = setOf(SyncableEntity.USER_SETTING),
            priority = SyncPriority.ELEVATED,
            direction = SyncDirection.UP,
        )

        if (!result.wasSuccessful) {
            logError("Failed to update notification settings from the sync. Will retry eventually")
        }

        return result.wasSuccessful
    }

    // We need a push notification to be saved when we parse it. This is because the clients are likely to
    // call markRead() shortly after receiving the push, and without it existing, the markRead will do nothing.
    // We will then, potentially, sync the notification data, and it will be unread.
    // We can't save a proper notification here because it could be a batched notification, and we won't have
    // a message for each individual, un-batched notification. The server COULD send the message to save for
    // each individual notification along with it. But that could get weird with our limited payload size.
    // And more importantly, I didn't think about it when I first did this, and now I am too lazy to want
    // to go change that code.
    // If the notification already exists, we can just ignore it. Presumably we synced the real notification
    // already in the background, or someone waited to push the push notification.
    private fun handlePushNotificationSave(notificationIds: List<UserNotificationId>, type: RawUserNotificationType) {
        val existingNotifications = userNotificationDao.findByIds(notificationIds).many().map { it.id }.toSet()

        notificationIds.forEach { notificationId ->
            if (!existingNotifications.contains(notificationId)) {
                // THere is a very small chance this could fail for an expected reason if the sync is happening
                // while we do this. Then the insert will encounter a duplicate key and fail. This IS desired,
                // as if the sync inserted a row, we do NOT want to replace it with this jank one.
                try {
                    userNotificationDao.insert(
                        DbUserNotification(
                            id = notificationId,
                            message = "", // We will exclude notifications with empty messages from the notification list
                            read = false,
                            createdAt = now(),
                            updatedAt = now(),
                            deleted = false,
                            type = type,
                            synchronized = true,
                            data_ = emptyMap(),
                        )
                    )
                } catch (e: Exception) {
                    logError("Insert of new push notification failed. ${e.message}")
                }
            }
        }
    }

    fun parsePushNotificationDataPayload(payload: Map<String, String>): UserNotificationData {
        try {
            logDebug("Parsing push notification data payload $payload")

            val notificationIds = payload["notification-ids"]?.let {
                ggJson.decodeFromString<List<UserNotificationId>>(it)
            } ?: emptyList()

            val rawType = payload["notification-type"]?.let { RawUserNotificationType(it) }
                ?: return NoActionNotificationData(notificationIds)

            val type = rawType.asEnumeratedType()

            if (notificationIds.isNotEmpty()) {
                handlePushNotificationSave(notificationIds, rawType)
            }

            val playlists = payload["playlists"]?.let {
                ggJson.decodeFromString<List<PlaylistResponse>>(it)
            }
            val playlistUsers = payload["playlist-users"]?.let {
                ggJson.decodeFromString<List<PlaylistUserResponse>>(it)
            }
            val addedTracks = payload["added-tracks"]?.let {
                ggJson.decodeFromString<List<TrackResponse>>(it)
            }
            val addedPlaylistTracks = payload["added-playlist-tracks"]?.let {
                ggJson.decodeFromString<List<PlaylistTrackResponse>>(it)
            }
            val removedPlaylistTracks = payload["removed-playlist-tracks"]?.let {
                ggJson.decodeFromString<List<Long>>(it)
            }
            val inputUsers = payload["input-users"]?.let {
                ggJson.decodeFromString<List<UserResponse>>(it)
            }
            val reviewSources = payload["review-sources"]?.let {
                ggJson.decodeFromString<List<ReviewSourceResponse>>(it)
            }

            if (inputUsers != null) {
                UserSyncStrategy.handleSave(EntityChangeContent(modified = inputUsers))
            }
            if (reviewSources != null) {
                ReviewSourceSyncStrategy.handleSave(EntityChangeContent(modified = reviewSources))
            }
            if (playlists != null) {
                PlaylistSyncStrategy.handleSave(EntityChangeContent(new = playlists))
            }
            if (playlistUsers != null) {
                // Though not IMPOSSIBLE to deal with, syncing multi-user playlists is very complex.
                // Doing handleSave() style logic here is not trivial, nor is it highly beneficial.
                // 1) We get added to a playlist. If this happens, the tracks on the playlist do not come through
                //    in the notification as there could be hundreds. We need to still sync in order to save the
                //    tracks. And if we save the PlaylistUser ahead of time, the sync might not realize that it's
                //    a new playlist add and fetch them. This is the most complex scenario.
                //
                // The other two scenarios are not as complex, but if we're not handling #1 anyway, why bother.
                // 2) We get our privilege changed between owner/reader/writer. This is not critical to do immediately.
                //    The user still has access to the playlist. They just can't see the updated management until
                //    it syncs properly, which is not a likely scenario for them to jump right into doing before a sync.
                // 3) A deletion happens. If we don't do this, then people won't see the playlist disappear right away.
                //    Big whoop. We still won't give them a shortcut to navigate to it so they might not even notice.
                SyncCoordinator.syncAsync(setOf(SyncableEntity.PLAYLIST_USER))
            }
            if (addedTracks != null) {
                TrackSyncStrategy.handleSave(EntityChangeContent(new = addedTracks))
            }
            if (addedPlaylistTracks != null || removedPlaylistTracks != null) {
                PlaylistTrackSyncStrategy.handleSave(EntityChangeContent(
                    new = addedPlaylistTracks ?: emptyList(),
                    removed = removedPlaylistTracks ?: emptyList(),
                ))
            }

            if (type == UserNotificationType.SHARED_PLAYLIST_RENAME && playlists?.isNotEmpty() == true) {
                return PlaylistNotificationData(notificationIds, playlists.first().id)
            }
            if (type == UserNotificationType.USER_RECOMMEND && addedTracks?.isNotEmpty() == true) {
                val apiId = addedTracks.first().id
                val track = trackDao.findByApiId(apiId).one()
                return TrackNotificationData(notificationIds, track = track.toTrack())
            }
            if (type == UserNotificationType.SHARED_PLAYLIST_TRACK_CHANGE && playlists?.isNotEmpty() == true) {
                return PlaylistNotificationData(notificationIds, playlists.first().id)
            }
            if (type == UserNotificationType.SHARED_PLAYLIST_ACCESS && playlistUsers?.isNotEmpty() == true) {
                playlistUsers.find {
                    it.ownershipType.asEnumeratedType() != PlaylistOwnershipType.NONE
                }?.let { relevantPlaylist ->
                    return PlaylistNotificationData(notificationIds, relevantPlaylist.playlistId)
                }

                // If we were removed from a playlist as the only action, then there's nothing to do.
                return NoActionNotificationData(notificationIds)
            }

            return NoActionNotificationData(notificationIds)
        } catch (e: Exception) {
            // There's a lot of decoding going on here from server data. Be safe and don't crash the apps
            // if there's a problem with any of the data
            logCrit("Failed to decode notification payload!", e)
            return NoActionNotificationData(emptyList())
        }
    }

    @Suppress("RedundantOverride", "unused") // Overrides needed for iOS convenience as it can't handle generic functions
    class UserNotificationEventRegistration : EventHandlerRegistration<UserNotificationChangeHandler>() {
        fun registerIos(handler: UserNotificationChangeHandler): Int = super.register(handler)
        override fun unregister(handlerId: Int) = super.unregister(handlerId)
    }

    val eventRegistration by lazy { UserNotificationEventRegistration() }
}

@Serializable
private data class NotificationSettingResponse(
    val items: List<NotificationSettingResponseItem>
)

@Serializable
private data class NotificationSettingResponseItem(
    val type: String,
    val displayString: String,
    val settingMethodKey: String,
    val settingBatchIntervalKey: String,
    val defaultBatchIntervalMinutes: Int,
    val defaultMethod: RawNotificationSettingConfiguration,
) {
    fun toNotificationSetting(): NotificationSetting {
        val userMethod = Settings.getString(settingMethodKey, defaultMethod.value)
        val userBatching = Settings.getInt(settingBatchIntervalKey, defaultBatchIntervalMinutes)
        return NotificationSetting(
            type = type,
            method = RawNotificationSettingConfiguration(userMethod).asEnumeratedType(),
            displayString = displayString,
            settingMethodKey = settingMethodKey,
            settingBatchIntervalKey = settingBatchIntervalKey,
            batchIntervalMinutes = userBatching,
        )
    }
}

data class NotificationSetting(
    val type: String,
    val displayString: String,
    internal val settingMethodKey: String,
    internal val settingBatchIntervalKey: String,
    var batchIntervalMinutes: Int,
    var method: NotificationSettingMethod,
)

enum class NotificationSettingMethod {
    OFF, SILENT, PUSH, UNKNOWN
}

@Serializable
@JvmInline
value class RawNotificationSettingConfiguration(val value: String) {
    fun asEnumeratedType(): NotificationSettingMethod {
        return try {
            NotificationSettingMethod.valueOf(value)
        } catch (_: Throwable) {
            this.logError("Unknown enumerated type value '$value' for enum '${NotificationSettingMethod::class.simpleName}'")
            NotificationSettingMethod.UNKNOWN
        }
    }
}

private val DbUserNotification.playlistIds: List<PlaylistId> get() {
    return this.data_["playlistIds"]?.let { ids -> (ids as List<*>).map { PlaylistId(it as Long) } }
        ?: emptyList()
}

private val DbUserNotification.addedTrackIds: List<TrackApiId> get() {
    return this.data_["addedTrackIds"]?.let { ids -> (ids as List<*>).map { TrackApiId(it as Long) } }
        ?: emptyList()
}